import { useEffect, useRef } from 'react'

import { trackEvent } from '@/utils/analytics/useAnalytics'

import { WINDOW_BLUR, WINDOW_FOCUS } from '@/utils/analytics/types/baseEvents'

const elapsedSeconds = (since: number) =>
  Math.round((Date.now() - since) / 1000)

// Tracks when the user leaves or returns to the HiveWatch browser window, including switching tabs or apps.
export function useWindowFocusTracking() {
  const blurredAtRef = useRef<number | null>(null)
  const focusedAtRef = useRef<number>(Date.now())

  useEffect(() => {
    const onBlur = () => {
      blurredAtRef.current = Date.now()
      trackEvent(WINDOW_BLUR, {
        active_seconds: elapsedSeconds(focusedAtRef.current),
      })
    }
    const onFocus = () => {
      // Only fire if the window previously lost focus — guards against false positives (e.g. initial page load).
      if (blurredAtRef.current === null) return
      const inactiveSeconds = elapsedSeconds(blurredAtRef.current)
      blurredAtRef.current = null
      focusedAtRef.current = Date.now()
      trackEvent(WINDOW_FOCUS, { inactive_seconds: inactiveSeconds })
    }

    window.addEventListener('blur', onBlur)
    window.addEventListener('focus', onFocus)

    return () => {
      window.removeEventListener('blur', onBlur)
      window.removeEventListener('focus', onFocus)
    }
  }, [])
}
